import { useTheme } from '@mui/material/styles';
import { FONTS } from '../../config/app-config';
import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ReplayIcon from "@mui/icons-material/Replay";


const BOARD_SIZE = 20;
const DEFAULT_DIFFICULTY = "Medium";
const LOG_FACTOR = 30;
const MAX_STEPS_PER_FRAME = 3;

const DIFFICULTIES = {
    Easy: { initialSpeed: 200, minSpeed: 50, scoreMult: 1 },
    Medium: { initialSpeed: 150, minSpeed: 40, scoreMult: 2 },
    Hard: { initialSpeed: 100, minSpeed: 30, scoreMult: 3 },
};

const DIRECTIONS = {
    ArrowUp: { x: 0, y: -1 },
    ArrowDown: { x: 0, y: 1 },
    ArrowLeft: { x: -1, y: 0 },
    ArrowRight: { x: 1, y: 0 },
};

const ROTATION = {
    ArrowUp: "0deg",
    ArrowRight: "90deg",
    ArrowDown: "180deg",
    ArrowLeft: "-90deg",
};

function isOpposite(a, b) {
    return (
        (a === "ArrowUp" && b === "ArrowDown") ||
        (a === "ArrowDown" && b === "ArrowUp") ||
        (a === "ArrowLeft" && b === "ArrowRight") ||
        (a === "ArrowRight" && b === "ArrowLeft")
    );
}

function getInitialSnake() {
    const centerX = Math.floor(BOARD_SIZE / 2);
    const centerY = Math.floor(BOARD_SIZE / 2);
    return [
        { x: centerX, y: centerY },
        { x: centerX - 1, y: centerY },
        { x: centerX - 2, y: centerY },
    ];
}

function getRandomFood(snake) {
    while (true) {
        const x = Math.floor(Math.random() * BOARD_SIZE);
        const y = Math.floor(Math.random() * BOARD_SIZE);
        if (!snake.some((segment) => segment.x === x && segment.y === y)) {
            return { x, y };
        }
    }
}

function getSpeed(difficulty, eatenCount) {
    const config = DIFFICULTIES[difficulty];
    const raw = config.initialSpeed - LOG_FACTOR * Math.log(1 + eatenCount);
    return Math.max(config.minSpeed, Math.round(raw));
}

function hexToRgb(hex) {
    const clean = hex.replace("#", "");
    const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean;
    const value = parseInt(full, 16);
    return {
        r: (value >> 16) & 255,
        g: (value >> 8) & 255,
        b: value & 255,
    };
}

function rgbToHex({ r, g, b }) {
    const toHex = (n) => n.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function lerp(a, b, t) {
    return Math.round(a + (b - a) * t);
}

function lerpColor(from, to, t) {
    const a = hexToRgb(from);
    const b = hexToRgb(to);
    return rgbToHex({
        r: lerp(a.r, b.r, t),
        g: lerp(a.g, b.g, t),
        b: lerp(a.b, b.b, t),
    });
}

function getSwipeDirection(dx, dy) {
    if (Math.abs(dx) > Math.abs(dy)) {
        return dx > 0 ? "ArrowRight" : "ArrowLeft";
    }
    return dy > 0 ? "ArrowDown" : "ArrowUp";
}

function createGameState(difficulty, direction = "ArrowRight") {
    const snake = getInitialSnake();
    return {
        snake,
        food: getRandomFood(snake),
        direction,
        score: 0,
        eatenCount: 0,
        speed: DIFFICULTIES[difficulty].initialSpeed,
        started: false,
        gameOver: false,
    };
}

function stepGame(game, difficulty) {
    const delta = DIRECTIONS[game.direction];
    const head = game.snake[0];
    const newHead = { x: head.x + delta.x, y: head.y + delta.y };
    const ate = newHead.x === game.food.x && newHead.y === game.food.y;
    const collisionBody = ate ? game.snake : game.snake.slice(0, -1);

    const hitWall =
        newHead.x < 0 ||
        newHead.x >= BOARD_SIZE ||
        newHead.y < 0 ||
        newHead.y >= BOARD_SIZE;

    const hitSelf = collisionBody.some(
        (segment) => segment.x === newHead.x && segment.y === newHead.y
    );

    let nextSnake = [newHead, ...game.snake];
    if (!ate) {
        nextSnake.pop();
    }

    if (hitWall || hitSelf) {
        return {
            ...game,
            snake: nextSnake,
            started: false,
            gameOver: true,
        };
    }

    if (!ate) {
        return {
            ...game,
            snake: nextSnake,
        };
    }

    const nextEatenCount = game.eatenCount + 1;
    return {
        ...game,
        snake: nextSnake,
        food: getRandomFood(nextSnake),
        score: game.score + DIFFICULTIES[difficulty].scoreMult,
        eatenCount: nextEatenCount,
        speed: getSpeed(difficulty, nextEatenCount),
    };
}

export default function SnakeGame() {
    const theme = useTheme();
    const [difficulty, setDifficulty] = useState(DEFAULT_DIFFICULTY);
    const [menuOpened, setMenuOpened] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [gameState, setGameState] = useState(() => createGameState(DEFAULT_DIFFICULTY));

    const gameRef = useRef(gameState);
    const difficultyRef = useRef(difficulty);
    const showMenuRef = useRef(showMenu);
    const dirQueueRef = useRef([]);
    const touchStartRef = useRef(null);
    const lastTimeRef = useRef(null);
    const accRef = useRef(0);

    useEffect(() => {
        gameRef.current = gameState;
    }, [gameState]);

    useEffect(() => {
        difficultyRef.current = difficulty;
    }, [difficulty]);

    useEffect(() => {
        showMenuRef.current = showMenu;
    }, [showMenu]);

    const openPreview = () => {
        const preview = createGameState(difficultyRef.current);
        setGameState(preview);
        gameRef.current = preview;
        setShowMenu(true);
        setMenuOpened(true);
        dirQueueRef.current = [];
        accRef.current = 0;
        lastTimeRef.current = null;
    };

    const startGame = (direction = "ArrowRight") => {
        const game = createGameState(difficultyRef.current, direction);
        const startedGame = { ...game, started: true, gameOver: false };
        setGameState(startedGame);
        gameRef.current = startedGame;
        setShowMenu(false);
        dirQueueRef.current = [];
        accRef.current = 0;
        lastTimeRef.current = null;
    };

    const queueDirection = (nextDirection) => {
        const currentGame = gameRef.current;
        const lastQueued =
            dirQueueRef.current.length > 0
                ? dirQueueRef.current[dirQueueRef.current.length - 1]
                : currentGame.direction;

        if (lastQueued === nextDirection || isOpposite(lastQueued, nextDirection)) {
            return;
        }

        if (dirQueueRef.current.length < 2) {
            dirQueueRef.current.push(nextDirection);
        }

        setGameState((prev) => ({
            ...prev,
            direction: nextDirection,
        }));
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(e.key)) {
                return;
            }

            if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
                e.preventDefault();
            }

            const game = gameRef.current;

            if (e.key === " ") {
                if (game.gameOver) {
                    startGame();
                    return;
                }
                if (!game.started && showMenuRef.current) {
                    startGame();
                }
                return;
            }

            if (!game.started) {
                if (showMenuRef.current && !isOpposite(game.direction, e.key)) {
                    startGame(e.key);
                }
                return;
            }

            if (e.repeat) return;
            queueDirection(e.key);
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    const handleTouchStart = (e) => {
        if (!e.touches || e.touches.length === 0) return;
        const touch = e.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (e) => {
        if (!touchStartRef.current || !e.changedTouches || e.changedTouches.length === 0) return;

        const touch = e.changedTouches[0];
        const dx = touch.clientX - touchStartRef.current.x;
        const dy = touch.clientY - touchStartRef.current.y;
        touchStartRef.current = null;

        const nextDirection = getSwipeDirection(dx, dy);
        const game = gameRef.current;

        if (!game.started) {
            if (showMenuRef.current && !isOpposite(game.direction, nextDirection)) {
                startGame(nextDirection);
            }
            return;
        }

        queueDirection(nextDirection);
    };

    useEffect(() => {
        let rafId = 0;

        const loop = (time) => {
            if (!lastTimeRef.current) {
                lastTimeRef.current = time;
            }

            const dt = time - lastTimeRef.current;
            lastTimeRef.current = time;

            const currentGame = gameRef.current;
            if (!currentGame.started || currentGame.gameOver) {
                accRef.current = 0;
                rafId = requestAnimationFrame(loop);
                return;
            }

            accRef.current += dt;

            let steps = 0;
            let workingGame = currentGame;

            while (accRef.current >= workingGame.speed && steps < MAX_STEPS_PER_FRAME) {
                if (dirQueueRef.current.length > 0) {
                    const candidate = dirQueueRef.current.shift();
                    if (
                        candidate &&
                        candidate !== workingGame.direction &&
                        !isOpposite(workingGame.direction, candidate)
                    ) {
                        workingGame = {
                            ...workingGame,
                            direction: candidate,
                        };
                    }
                }

                workingGame = stepGame(workingGame, difficultyRef.current);
                accRef.current -= workingGame.speed;
                steps += 1;

                if (workingGame.gameOver) {
                    accRef.current = 0;
                    break;
                }
            }

            if (steps === MAX_STEPS_PER_FRAME && accRef.current > workingGame.speed) {
                accRef.current = 0;
            }

            if (workingGame !== currentGame) {
                gameRef.current = workingGame;
                setGameState(workingGame);
            }

            rafId = requestAnimationFrame(loop);
        };

        rafId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(rafId);
    }, []);

    const snakeIndexMap = useMemo(() => {
        const map = new Map();
        gameState.snake.forEach((segment, index) => {
            map.set(`${segment.x}-${segment.y}`, index);
        });
        return map;
    }, [gameState.snake]);

    const headTileColor = theme.palette.primary.main || "#001738";
    const tailTileColor = "#eaf6ff";
    const headRotation = ROTATION[gameState.direction] || "0deg";

    const cells = useMemo(() => {
        const items = [];

        for (let y = 0;y < BOARD_SIZE;y++) {
            for (let x = 0;x < BOARD_SIZE;x++) {
                const segIndex = snakeIndexMap.get(`${x}-${y}`);
                const isHead = segIndex === 0;
                const isBody = segIndex > 0;
                const isFood = gameState.food.x === x && gameState.food.y === y;

                if (isHead) {
                    items.push(
                        <Box
                            key={`${x}-${y}`}
                            sx={{ position: "relative", width: "100%", height: "100%" }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    bgcolor: headTileColor,
                                }}
                            />
                            <Box
                                component="img"
                                src="img/snake-face.png"
                                alt="snake head"
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    pointerEvents: "none",
                                    transform: `rotate(${headRotation})`,
                                    transition: "none",
                                }}
                            />
                        </Box>
                    );
                } else {
                    let bgcolor = "#fff";

                    if (isBody) {
                        const denom = Math.max(1, gameState.snake.length - 1);
                        const t = 1 - segIndex / denom;
                        bgcolor = lerpColor(tailTileColor, headTileColor, t);
                    } else if (isFood) {
                        bgcolor = "#f15a22";
                    }

                    items.push(
                        <Box
                            key={`${x}-${y}`}
                            sx={{ width: "100%", height: "100%", bgcolor }}
                        />
                    );
                }
            }
        }

        return items;
    }, [gameState.snake, gameState.food, snakeIndexMap, headRotation, headTileColor]);

    const showBoard = gameState.started || showMenu || gameState.gameOver;
    const showDifficultyButtons = (showMenu || gameState.gameOver) && !gameState.started;

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: 600,
                mx: "auto",
                my: 2,
                px: 2,
                textAlign: "center",
            }}
        >
            {showBoard && (
                <Box display="flex" justifyContent="center" mb={1}>
                    <Typography sx={{ fontFamily: FONTS.body }}>
                        Score:
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONTS.body,
                            fontWeight: "bold",
                            ml: 1,
                        }}
                    >
                        {gameState.score}
                        <span style={{ opacity: 0.8 }}>
                            {" "}
                            (x{DIFFICULTIES[difficulty].scoreMult})
                        </span>
                    </Typography>
                </Box>
            )}

            {!menuOpened && (
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        size="large"
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            fontFamily: FONTS.body,
                        }}
                        onClick={openPreview}
                    >
                        Can I play some Snake instead?
                    </Button>
                </Box>
            )}

            {showBoard && (
                <Box
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    tabIndex={0}
                    sx={{
                        outline: "none",
                        position: "relative",
                        width: "100%",
                        aspectRatio: "1",
                        border: "5px solid #202d4f",
                        bgcolor: "#ffffff",
                        "&::before": { content: "''", float: "left", paddingTop: "100%" },
                        "&::after": { content: "''", display: "block", clear: "both" },
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            gridTemplateColumns: `repeat(${BOARD_SIZE}, 1fr)`,
                            gridTemplateRows: `repeat(${BOARD_SIZE}, 1fr)`,
                        }}
                    >
                        {cells}
                    </Box>
                </Box>
            )}

            {showDifficultyButtons && (
                <Box sx={{ mt: 2, display: "flex", gap: 1, justifyContent: "center" }}>
                    {Object.keys(DIFFICULTIES).map((level) => (
                        <Button
                            key={level}
                            variant={difficulty === level ? "contained" : "outlined"}
                            onClick={() => setDifficulty(level)}
                            sx={{ bgcolor: 'accent.main', color: difficulty === level ? "#ffffff" : "#808080", fontFamily: FONTS.body }}
                        >
                            {level}
                        </Button>
                    ))}
                </Box>
            )}

            {gameState.gameOver && (
                <Box
                    sx={{
                        mt: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{ mr: 1, fontFamily: FONTS.heading }}
                    >
                        Game Over
                    </Typography>
                    <IconButton onClick={() => startGame()}>
                        <ReplayIcon />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}